<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link
        view="AdminReimbursementEmployerList"
        label="Reimbursement Employers"
        :active="true"
      />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <div>
              <h1 class="title mb-2">Manage Reimbursement Employers</h1>
              <p class="subtitle is-6 has-text-grey mt-0">Total: {{ totalCount }} employers</p>
            </div>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button type="is-success" @click="openCreateModal">
              <b-icon icon="plus"></b-icon>
              <span>Create New</span>
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <!-- Search -->
      <b-field>
        <div class="field-body">
          <b-field label="Search employers">
            <b-input
              v-model="search"
              placeholder="Search by name..."
              icon="magnify"
              @input="debouncedSearch"
            ></b-input>
          </b-field>
        </div>
      </b-field>

      <div class="table-container">
        <b-table
          :data="employers"
          :loading="loading"
          hoverable
          striped
          :paginated="true"
          backend-pagination
          :total="totalCount"
          :per-page="perPage"
          :current-page="currentPage"
          @page-change="onPageChange"
          pagination-position="bottom"
          default-sort="name"
          default-sort-direction="asc"
        >
          <b-table-column field="name" label="Name" v-slot="props" sortable>
            <span class="has-text-weight-semibold">{{ props.row.name }}</span>
          </b-table-column>

          <b-table-column label="Actions" width="160" v-slot="props">
            <div class="field is-grouped">
              <div class="control">
                <b-button
                  type="is-primary is-outlined"
                  size="is-small"
                  icon-left="pencil"
                  @click="openEditModal(props.row)"
                >
                  Edit
                </b-button>
              </div>
              <div class="control">
                <b-button
                  type="is-danger"
                  size="is-small"
                  icon-left="delete"
                  @click="confirmDelete(props.row)"
                >
                  Delete
                </b-button>
              </div>
            </div>
          </b-table-column>

          <template #empty>
            <div class="has-text-centered">
              <b-icon icon="emoticon-sad" size="is-large"></b-icon>
              <p>No employers found.</p>
            </div>
          </template>
        </b-table>
      </div>

      <!-- Create Modal -->
      <b-modal v-model="isCreateModalActive" :width="500" scroll="keep">
        <form @submit.prevent="createEmployer">
          <div class="modal-card">
            <header class="modal-card-head">
              <p class="modal-card-title">Create New Employer</p>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="isCreating"></b-loading>

              <b-field label="Name" required>
                <b-input
                  v-model="createForm.name"
                  placeholder="Enter employer name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="closeCreateModal">Cancel</b-button>
              <b-button type="is-success" native-type="submit" :loading="isCreating">
                Create
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>

      <!-- Edit Modal -->
      <b-modal v-model="showEditModal" :width="500" scroll="keep">
        <form @submit.prevent="handleUpdateEmployer">
          <div class="modal-card" style="width: auto">
            <header class="modal-card-head">
              <p class="modal-card-title">Edit Employer</p>
              <button type="button" class="delete" @click="showEditModal = false"></button>
            </header>
            <section class="modal-card-body">
              <b-loading :is-full-page="false" v-model="editLoading"></b-loading>

              <b-field label="Name" :type="{ 'is-danger': !!errors.name }" :message="errors.name">
                <b-input
                  v-model="editForm.name"
                  placeholder="Enter employer name"
                  required
                  maxlength="255"
                ></b-input>
              </b-field>
            </section>
            <footer class="modal-card-foot">
              <b-button @click="showEditModal = false">Cancel</b-button>
              <b-button type="is-primary" native-type="submit" :loading="editLoading">
                Save Changes
              </b-button>
            </footer>
          </div>
        </form>
      </b-modal>
    </div>
  </AdminLayout>
</template>

<script>
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminReimbursementEmployerList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: false,
      employers: [],
      totalCount: 0,
      currentPage: 1,
      perPage: 50,
      search: '',
      searchTimeout: null,
      isCreateModalActive: false,
      createForm: {
        name: '',
      },
      isCreating: false,
      showEditModal: false,
      editLoading: false,
      editingEmployer: null,
      editForm: {},
      errors: {},
    };
  },
  created() {
    this.loadEmployers();
  },
  methods: {
    async loadEmployers() {
      this.loading = true;
      try {
        const params = new URLSearchParams();
        if (this.perPage) params.append('limit', this.perPage);
        if (this.currentPage > 1) params.append('page', this.currentPage);
        if (this.search.trim()) params.append('name', this.search.trim());

        const response = await request.get(`/admin/reimbursements/employers?${params.toString()}`);
        this.employers = response.data.employers || [];
        this.totalCount = parseInt(response.headers['x-total-count'] || '0');
      } catch (error) {
        console.error('Error loading employers:', error);
        this.$buefy.toast.open({
          message: 'Failed to load employers',
          type: 'is-danger',
        });
      } finally {
        this.loading = false;
      }
    },

    onPageChange(page) {
      this.currentPage = page;
      this.loadEmployers();
    },

    debouncedSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
        this.loadEmployers();
      }, 300);
    },

    openCreateModal() {
      this.createForm = {
        name: '',
      };
      this.isCreateModalActive = true;
    },

    closeCreateModal() {
      this.isCreateModalActive = false;
      this.createForm = {
        name: '',
      };
    },

    async createEmployer() {
      this.isCreating = true;
      try {
        await request.post('/admin/reimbursements/employers', this.createForm);

        this.$buefy.toast.open({
          message: 'Employer created successfully',
          type: 'is-success',
        });

        this.closeCreateModal();
        this.loadEmployers();
      } catch (error) {
        console.error('Error creating employer:', error);
        this.$buefy.toast.open({
          message: error.response?.data?.message || 'Failed to create employer',
          type: 'is-danger',
        });
      } finally {
        this.isCreating = false;
      }
    },

    openEditModal(employer) {
      this.editingEmployer = employer;
      this.editForm = {
        name: employer.name,
      };
      this.errors = {};
      this.showEditModal = true;
    },

    async handleUpdateEmployer() {
      this.editLoading = true;
      this.errors = {};

      try {
        const payload = {
          name: this.editForm.name?.trim(),
        };

        await request.put(`/admin/reimbursements/employers/${this.editingEmployer.id}`, payload);

        this.$buefy.toast.open({
          message: 'Employer updated successfully',
          type: 'is-success',
        });

        this.showEditModal = false;
        this.loadEmployers();
      } catch (error) {
        console.error('Error updating employer:', error);
        if (error.response?.data?.errors) {
          this.errors = error.response.data.errors;
        } else if (error.response?.data?.message) {
          this.$buefy.toast.open({
            message: error.response.data.message,
            type: 'is-danger',
          });
        } else {
          this.$buefy.toast.open({
            message: 'Failed to update employer',
            type: 'is-danger',
          });
        }
      } finally {
        this.editLoading = false;
      }
    },

    confirmDelete(employer) {
      this.$buefy.dialog.confirm({
        title: 'Delete Employer',
        message: `Are you sure you want to delete "<strong>${employer.name}</strong>"? This action cannot be undone.`,
        confirmText: 'Delete',
        type: 'is-danger',
        hasIcon: true,
        onConfirm: () => this.deleteEmployer(employer),
      });
    },

    async deleteEmployer(employer) {
      try {
        await request.delete(`/admin/reimbursements/employers/${employer.id}`);

        this.$buefy.toast.open({
          message: 'Employer deleted successfully',
          type: 'is-success',
        });

        this.loadEmployers();
      } catch (error) {
        console.error('Error deleting employer:', error);
        let errorMessage = 'Failed to delete employer';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'Cannot delete this employer - it may still be in use by reimbursements';
        }

        // Show error message in a dialog for better visibility
        this.$buefy.dialog.alert({
          title: 'Delete Failed',
          message: errorMessage,
          type: 'is-danger',
          hasIcon: true,
          ariaRole: 'alertdialog',
          ariaModal: true,
        });
      }
    },
  },
};
</script>
